import * as React from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  ColumnPinningState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  type Table,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';

// Extend the ColumnMeta type to include initiallyHidden
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    initiallyHidden?: boolean;
    initiallyPinning?: 'left' | 'right';
  }
}

export interface TableRef<TData> {
  clearRowSelection: () => void;
  // Utility functions
  exportData: () => TData[];

  getCanNextPage: () => boolean;
  getCanPreviousPage: () => boolean;
  getColumnFilters: () => ColumnFiltersState;
  // Column pinning controls
  getColumnPinning: () => ColumnPinningState;
  getColumnVisibility: () => VisibilityState;
  getCurrentColumns: () => ColumnDef<TData>[];
  getCurrentData: () => TData[];
  getCurrentPage: () => number;

  getFilteredRows: () => TData[];
  getPageCount: () => number;
  getSelectedRowIds: () => string[];

  // Row selection
  getSelectedRows: () => TData[];
  getSorting: () => SortingState;
  // Table state
  getTableState: () => {
    pagination: PaginationState;
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    columnVisibility: VisibilityState;
    rowSelection: Record<string, boolean>;
    columnPinning: ColumnPinningState;
  };
  getTotalRows: () => number;

  // Pagination controls
  nextPage: () => void;
  pinColumn: (columnId: string, position: 'left' | 'right' | false) => void;
  previousPage: () => void;

  // Data management
  refreshData: (newData: TData[]) => void;
  resetColumnPinning: () => void;
  resetFilters: () => void;
  resetSorting: () => void;
  selectAllRows: () => void;

  // Filtering controls
  setColumnFilters: (filters: ColumnFiltersState) => void;
  setColumnPinning: (pinning: ColumnPinningState) => void;

  // Column visibility
  setColumnVisibility: (visibility: VisibilityState) => void;

  setGlobalFilter: (filter: string) => void;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  
  // Sorting controls
  setSorting: (sorting: SortingState) => void;
  toggleColumnVisibility: (columnId: string) => void;
  toggleRowSelection: (rowId: string) => void;
  // Column management
  updateColumns: (newColumns: ColumnDef<TData>[]) => void;
}

export type UseTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  getRowId: (row: TData) => string;
  enableColumnResizing?: boolean;
};

export function useTable<TData>({
  getRowId,
  data: initialData,
  columns: initialColumns,
  enableColumnResizing = false,
}: UseTableProps<TData>) {
  const [data, setData] = React.useState(() => initialData);
  const [columns, setColumns] = React.useState<ColumnDef<TData>[]>(() => initialColumns);
  const [rowSelection, setRowSelection] = React.useState({});
  const [globalFilter, setGlobalFilter] = React.useState('');

  // Initialize column visibility based on meta.initiallyHidden
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => {
    const initialVisibility: VisibilityState = {};
    columns.forEach((column) => {
      if (column.meta?.initiallyHidden) {
        initialVisibility[column.id || (column as any).accessorKey] = false;
      }
    });
    return initialVisibility;
  });

  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(() => {
    const leftPinnedColumns = columns.filter((column) => column.meta?.initiallyPinning === 'left');
    const rightPinnedColumns = columns.filter((column) => column.meta?.initiallyPinning === 'right');
    return {
      left: leftPinnedColumns.map((c) => c.id || (c as any).accessorKey),
      right: rightPinnedColumns.map((c) => c.id || (c as any).accessorKey),
    };
  });
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Create the table instance
  const table: Table<TData> = useReactTable({
    data,
    columns,
    getRowId,
    enableRowSelection: true,
    enableGlobalFilter: true,
    enableColumnPinning: true,
    onSortingChange: setSorting,
    columnResizeMode: 'onChange',
    globalFilterFn: 'includesString',
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnPinningChange: setColumnPinning,
    getFacetedRowModel: getFacetedRowModel(),
    enableColumnResizing: enableColumnResizing,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      pagination,
      rowSelection,
      globalFilter,
      columnPinning,
      columnFilters,
      columnVisibility,
    },
  });

  // Update data if initialData changes
  React.useEffect(() => {
    setData(initialData);
  }, [initialData]);

  // Update columns if initialColumns changes
  React.useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  return {
    data,
    table,
    setData,
    setSorting,
    setGlobalFilter,

    setColumnFilters,
    setColumnVisibility,
    updateColumns: setColumns,
    getSorting: () => sorting,
    getCurrentData: () => data,
    getTotalRows: () => data.length,
    getCurrentColumns: () => columns,
    nextPage: () => table.nextPage(),

    resetSorting: () => setSorting([]),
    getColumnFilters: () => columnFilters,
    // Column pinning methods
    getColumnPinning: () => columnPinning,

    previousPage: () => table.previousPage(),
    getPageCount: () => table.getPageCount(),
    resetFilters: () => setColumnFilters([]),
    getColumnVisibility: () => columnVisibility,

    getCanNextPage: () => table.getCanNextPage(),
    clearRowSelection: () => setRowSelection({}),
    getSelectedRowIds: () => Object.keys(rowSelection),

    refreshData: (newData: TData[]) => setData(newData),
    getCanPreviousPage: () => table.getCanPreviousPage(),
    selectAllRows: () => table.toggleAllRowsSelected(true),
    getCurrentPage: () => table.getState().pagination.pageIndex,
    setPageSize: (pageSize: number) => table.setPageSize(pageSize),

    setPageIndex: (pageIndex: number) => table.setPageIndex(pageIndex),
    resetColumnPinning: () => setColumnPinning({ left: [], right: [] }),
    toggleRowSelection: (rowId: string) => table.getRow(rowId)?.toggleSelected(),
    setColumnPinning: (pinning: ColumnPinningState) => setColumnPinning(pinning),
    exportData: () => table.getFilteredRowModel().rows.map((row) => row.original),
    getSelectedRows: () => table.getSelectedRowModel().rows.map((row) => row.original),
    
    getFilteredRows: () => table.getFilteredRowModel().rows.map((row) => row.original),
    getTableState: () => ({
      sorting,
      pagination,
      rowSelection,
      columnFilters,
      columnPinning,
      columnVisibility,
    }),
    toggleColumnVisibility: (columnId: string) => {
      setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: !prev[columnId],
      }));
    },
    pinColumn: (columnId: string, position: 'left' | 'right' | false) => {
      const column = table.getColumn(columnId);
      if (column) {
        column.pin(position);
      }
    },
  };
}

export const useTableWithRef = <TData>(props: UseTableProps<TData>, ref: React.Ref<TableRef<TData>>) => {
  const tableResult = useTable(props);

  React.useImperativeHandle(
    ref,
    () => ({
      // Pagination controls
      nextPage: tableResult.nextPage,
      pinColumn: tableResult.pinColumn,

      // Sorting controls
      setSorting: tableResult.setSorting,
      getSorting: tableResult.getSorting,
      // Utility functions
      exportData: tableResult.exportData,
      setPageSize: tableResult.setPageSize,
      // Data management
      refreshData: tableResult.refreshData,
      previousPage: tableResult.previousPage,
      setPageIndex: tableResult.setPageIndex,
      getPageCount: tableResult.getPageCount,

      resetSorting: tableResult.resetSorting,
      resetFilters: tableResult.resetFilters,
      getTotalRows: tableResult.getTotalRows,

      updateColumns: tableResult.updateColumns,
      selectAllRows: tableResult.selectAllRows,
      // Table state
      getTableState: tableResult.getTableState,
      getCanNextPage: tableResult.getCanNextPage,

      getCurrentPage: tableResult.getCurrentPage,
      getCurrentData: tableResult.getCurrentData,
      setGlobalFilter: tableResult.setGlobalFilter,

      // Row selection
      getSelectedRows: tableResult.getSelectedRows,
      getFilteredRows: tableResult.getFilteredRows,
      // Filtering controls
      setColumnFilters: tableResult.setColumnFilters,
      getColumnFilters: tableResult.getColumnFilters,
      // Column pinning
      getColumnPinning: tableResult.getColumnPinning,

      setColumnPinning: tableResult.setColumnPinning,
      getCurrentColumns: tableResult.getCurrentColumns,

      getSelectedRowIds: tableResult.getSelectedRowIds,

      clearRowSelection: tableResult.clearRowSelection,
      getCanPreviousPage: tableResult.getCanPreviousPage,
      toggleRowSelection: tableResult.toggleRowSelection,
      
      resetColumnPinning: tableResult.resetColumnPinning,
      // Column visibility
      setColumnVisibility: tableResult.setColumnVisibility,
      getColumnVisibility: tableResult.getColumnVisibility,
      toggleColumnVisibility: tableResult.toggleColumnVisibility,
    }),
    [tableResult],
  );

  return tableResult;
};
